import { STORAGE_SERVICE_KEYS } from '@/utils/constants';
import { unixTsNow, unixTsToFormatted } from '@/utils/unix';
import { defineStore } from 'pinia';

type NullableNumber = number | null;

/** Minimum delay between two activity updates (in seconds) */
const ACTIVITY_DEBOUNCE_SECONDS = 5;

/** DOM events considered as user activity */
const ACTIVITY_EVENTS = ['click', 'keydown', 'keypress'] as const;

/** Structure of session data stored in localStorage (shared across tabs) */
interface StoredSessionData {
  lastActivity: number | null;
  lastAutoRefresh: number | null;
  lastActivityFormatted: string;
  lastAutoRefreshFormatted: string;
}

/** Auth payload stored after login (replaces former localStorage user_auth) */
export interface StoredUserAuth {
  access_token: string;
  refresh_token: string;
  id_token: string;
  expiry: string;
}

/** User profile from Keycloak loadUserInfo (replaces former localStorage user_profile) */
export type StoredUserProfile = Record<string, unknown>;

export const useTokenStore = defineStore('token', {
  state: () => ({
    user: null as string | null,

    token: '',
    tokenExpiry: 0,
    tokenRefreshExpiry: 0,

    isUserLogged: false,

    authTime: null as NullableNumber,
    sessionStartTime: null as NullableNumber,
    tokenExpirationTime: null as NullableNumber,
    refreshTokenExpirationTime: null as NullableNumber,

    lastActivity: null as NullableNumber,
    lastAutoRefresh: null as NullableNumber,

    /** User profile from Keycloak (loadUserInfo). Replaces localStorage user_profile. */
    userProfile: null as StoredUserProfile | null,
    /** Auth tokens and expiry. Replaces localStorage user_auth. */
    userAuth: null as StoredUserAuth | null,

    showDebugger: false as boolean,
    // Handlers stored to allow removeEventListener cleanup
    _activityHandler: null as ((e?: Event) => void) | null,
    _storageHandler: null as ((e: StorageEvent) => void) | null,

    /** Injected callback for automatic refresh attempts (checks expiration + activity) */
    _onRefreshNeeded: null as (() => Promise<boolean>) | null,
  }),

  getters: {
    lastActivityFormatted: state =>
      state.lastActivity ? unixTsToFormatted(state.lastActivity) : null,
    lastAutoRefreshFormatted: state =>
      state.lastAutoRefresh ? unixTsToFormatted(state.lastAutoRefresh) : null,
    isSessionActive: state => !!state.user,
    getIsUserLogged: state => state.isUserLogged,
  },

  actions: {
    toggleDebugger() {
      this.showDebugger = !this.showDebugger;
    },

    /** Registers the refresh callback - called by keycloakService at login */
    registerRefreshCallback(callback: () => Promise<boolean>) {
      this._onRefreshNeeded = callback;
    },

    async startTokenActivity(
      username: string | null,
      auth_time: NullableNumber,
      iat: NullableNumber,
      exp: NullableNumber,
      refreshExp: NullableNumber,
      isInit = false
    ) {
      if (isInit) {
        this.initActivityListeners();
        // Login or page refresh is considered as user activity
        await this.updateLastActivity(iat ?? undefined);
      }

      this.user = username;
      this.authTime = auth_time;
      this.sessionStartTime = iat;
      this.tokenExpirationTime = exp;
      this.refreshTokenExpirationTime = refreshExp;

      try {
        sessionStorage.setItem('EXP-A', unixTsToFormatted(exp));
        sessionStorage.setItem('EXP-B', unixTsToFormatted(refreshExp));
      } catch {
        // ignore storage errors
      }
    },

    async updateLastActivity(forceValue?: number) {
      const now = forceValue ?? unixTsNow();

      if (this.lastActivity !== null) {
        const secondsFromLastActivity = now - this.lastActivity;
        if (secondsFromLastActivity < ACTIVITY_DEBOUNCE_SECONDS) return;
      }

      this.lastActivity = now;

      if (this.lastAutoRefresh) {
        this.lastAutoRefresh = null;
      }
      this.saveSessionToStorage();

      // Call the callback that handles all refresh logic:
      // - Checks if token is expired
      // - Validates activity is within session range
      // - Performs refresh if needed
      if (this._onRefreshNeeded) {
        await this._onRefreshNeeded();
      }
    },

    updateLastAutoRefresh(tokenIat: NullableNumber) {
      if (this.lastAutoRefresh) return;
      this.lastAutoRefresh = tokenIat;
      this.saveSessionToStorage();
    },

    initActivityListeners() {
      if (this._activityHandler) return; // Already initialized

      this._activityHandler = (_e?: Event) => {
        // Call asynchronously to avoid blocking the event
        void this.updateLastActivity();
      };
      this._storageHandler = async (e: StorageEvent) => {
        // Only react to our session key
        const key = STORAGE_SERVICE_KEYS.KC_ACTIVITY.KEYCLOAK_SESSION.key;
        if (e.key !== key) return;
        if (!e.newValue) return;

        let stored: StoredSessionData | null = null;
        try {
          stored = JSON.parse(e.newValue) as StoredSessionData;
        } catch {
          return;
        }

        this.lastActivity = stored.lastActivity ?? this.lastActivity;
        this.lastAutoRefresh = stored.lastAutoRefresh ?? this.lastAutoRefresh;

        // Call callback to handle refresh logic (cross-tab sync)
        if (this._onRefreshNeeded) {
          await this._onRefreshNeeded();
        }
      };

      ACTIVITY_EVENTS.forEach(evt =>
        document.addEventListener(evt, this._activityHandler!)
      );
      window.addEventListener('storage', this._storageHandler);
    },

    stopActivityListeners() {
      if (!this._activityHandler) return;
      ACTIVITY_EVENTS.forEach(evt =>
        document.removeEventListener(evt, this._activityHandler!)
      );
      if (this._storageHandler) {
        window.removeEventListener('storage', this._storageHandler);
      }
      this._activityHandler = null;
      this._storageHandler = null;
    },

    saveSessionToStorage() {
      this.lastActivity = this.lastAutoRefresh ?? unixTsNow();

      const sessionData = {
        lastActivity: this.lastActivity,
        lastAutoRefresh: this.lastAutoRefresh,
        lastActivityFormatted: unixTsToFormatted(this.lastActivity),
        lastAutoRefreshFormatted: unixTsToFormatted(this.lastAutoRefresh),
      };

      try {
        localStorage.setItem(
          STORAGE_SERVICE_KEYS.KC_ACTIVITY.KEYCLOAK_SESSION.key,
          JSON.stringify(sessionData)
        );
      } catch {
        // ignore
      }
    },

    clearSessionFromStorage() {
      localStorage.removeItem(
        STORAGE_SERVICE_KEYS.KC_ACTIVITY.KEYCLOAK_SESSION.key
      );
    },
    setToken(token: string) {
      this.token = token;
    },
    setTokenExpiry(expiry: number) {
      this.tokenExpiry = expiry;
    },
    setTokenRefreshExpiry(expiry: number) {
      this.tokenRefreshExpiry = expiry;
    },

    setIsUserLogged(isLogged: boolean) {
      this.isUserLogged = isLogged;
    },

    setUserProfile(profile: StoredUserProfile | null) {
      this.userProfile = profile;
    },

    setUserAuth(auth: StoredUserAuth | null) {
      this.userAuth = auth;
    },

    /** Clears profile, auth and session data (e.g. on logout). */
    clearAuthData() {
      this.userProfile = null;
      this.userAuth = null;
      this.clearSessionFromStorage();
    },
  },
});
